"use client";

import { useState, useCallback } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  CreditCard,
  Shield,
  ExternalLink,
  AlertCircle,
  Loader2,
  CalendarDays,
  Users,
  IndianRupee,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { SITE } from "@/lib/constants";

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill: { name: string; email: string; contact: string };
  theme: { color: string };
  handler: (response: RazorpayResponse) => void;
  modal: { ondismiss: () => void };
}

interface RazorpayInstance {
  open: () => void;
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

const formatINR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

interface BookingData {
  checkIn: Date;
  checkOut: Date;
  nights: number;
  guests: number;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  total: number;
}

interface PaymentStepProps {
  bookingData: BookingData;
  amount: number;
  razorpayKeyId: string;
  orderId: string;
  onSuccess: (paymentData: RazorpayResponse) => void;
  onBack: () => void;
}

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export function PaymentStep({
  bookingData,
  amount,
  razorpayKeyId,
  orderId,
  onSuccess,
  onBack,
}: PaymentStepProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePayment = useCallback(async () => {
    setError(null);
    setLoading(true);

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError("Failed to load payment gateway. Please check your connection and try again.");
      setLoading(false);
      return;
    }

    const options: RazorpayOptions = {
      key: razorpayKeyId,
      amount: amount * 100,
      currency: "INR",
      name: SITE.name,
      description: `Booking: ${format(bookingData.checkIn, "MMM d")} – ${format(bookingData.checkOut, "MMM d, yyyy")}`,
      order_id: orderId,
      prefill: {
        name: bookingData.guestName,
        email: bookingData.guestEmail,
        contact: bookingData.guestPhone,
      },
      theme: { color: "#B8860B" },
      handler: (response) => {
        setLoading(false);
        onSuccess(response);
      },
      modal: {
        ondismiss: () => {
          setLoading(false);
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      setError("Could not open payment window. Please try again.");
      setLoading(false);
    }
  }, [razorpayKeyId, amount, orderId, bookingData, onSuccess]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-secondary/30 p-5 space-y-4">
        <h3 className="font-playfair text-lg font-semibold text-foreground">
          Booking Summary
        </h3>

        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <CalendarDays className="size-4 text-primary shrink-0" />
            <div>
              <span className="text-foreground font-medium">
                {format(bookingData.checkIn, "EEE, MMM d")} –{" "}
                {format(bookingData.checkOut, "EEE, MMM d, yyyy")}
              </span>
              <span className="text-muted-foreground ml-2">
                ({bookingData.nights} {bookingData.nights === 1 ? "night" : "nights"})
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <Users className="size-4 text-primary shrink-0" />
            <span className="text-foreground">
              {bookingData.guests} {bookingData.guests === 1 ? "Guest" : "Guests"}
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <IndianRupee className="size-4 text-primary shrink-0" />
            <span className="text-foreground">
              Total: {formatINR.format(bookingData.total)}
            </span>
          </div>
        </div>

        <Separator className="bg-border/50" />

        <div className="flex items-center justify-between">
          <span className="font-semibold text-foreground">Amount to Pay Now</span>
          <span className="text-xl font-bold text-primary">
            {formatINR.format(amount)}
          </span>
        </div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4"
        >
          <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-destructive">{error}</p>
            <button
              type="button"
              onClick={handlePayment}
              className="mt-1 text-sm text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        </motion.div>
      )}

      <Button
        onClick={handlePayment}
        disabled={loading}
        className="w-full h-12 bg-primary text-primary-foreground text-base font-semibold hover:bg-primary/90 gold-glow rounded-xl"
      >
        {loading ? (
          <>
            <Loader2 className="size-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="size-5" />
            Pay {formatINR.format(amount)} Advance
          </>
        )}
      </Button>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Shield className="size-3.5" />
        <span>Secured by Razorpay. 256-bit encryption.</span>
      </div>

      <Separator className="bg-border/50" />

      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Prefer to book on Airbnb instead?
        </p>
        <Button
          variant="outline"
          asChild
          className="border-border text-foreground hover:bg-secondary"
        >
          <a
            href={SITE.airbnbUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="size-4" />
            Pay on Airbnb
          </a>
        </Button>
      </div>

      <div className="pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="w-full text-muted-foreground hover:text-foreground"
        >
          Back to Guest Details
        </Button>
      </div>
    </div>
  );
}
